import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Loader2 } from "lucide-react";
import api from "@/lib/api";

const fmtDate = (d) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString() : "—");

export default function AccountMonthlyBox() {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/subscriptions/me").then(({ data: d }) => setData(d)).catch(() => setData({ subscription: null })), []);
  useEffect(() => { load(); }, [load]);

  async function cancel() {
    const s = data.subscription;
    if (!window.confirm(`Cancel ${s.plan_name}? Your box stays active through your paid period, then billing stops.`)) return;
    setBusy(true); setMsg("");
    try { const { data: r } = await api.post("/subscriptions/cancel", { subscription_id: s.subscription_ref }); setMsg(r.message || "Canceled."); load(); }
    catch (e) { setMsg(e?.response?.data?.detail?.message || "Cancellation failed. Please contact support."); }
    finally { setBusy(false); }
  }

  if (!data) return <div className="p-8 text-white/50"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  const s = data.subscription;
  return (
    <div className="max-w-2xl mx-auto p-4 lg:p-8 text-white" data-testid="account-monthly-box">
      <div className="flex items-center gap-2 mb-6"><Package className="h-6 w-6 text-lifted-green" />
        <h1 className="font-anton uppercase text-3xl">My Monthly Box</h1></div>
      {data.complimentary && <div className="glass-panel p-3 mb-4 text-sm text-lifted-green" data-testid="mbx-complimentary">Your account has complimentary Monthly Box eligibility.</div>}
      {!s ? (
        <div className="glass-panel p-6 text-center" data-testid="mbx-none">
          <p className="text-white/70">You don't have a Monthly Box subscription.</p>
          <Link to="/lifted-monthly-box" className="btn-lifted mt-4 inline-flex" data-testid="mbx-browse">See the boxes</Link>
        </div>
      ) : (
        <div className="glass-panel p-6 space-y-2 text-sm" data-testid="mbx-subscription">
          <Row k="Plan" v={`${s.plan_name} (${s.tier_label})`} t="mbx-plan" />
          <Row k="Provider" v={s.provider === "paypal" ? "PayPal" : "Square"} t="mbx-provider" />
          <Row k="Recurring amount" v={s.amount_cents ? `$${(s.amount_cents / 100).toFixed(2)}/month` : "—"} t="mbx-amount" />
          <Row k="Status" v={s.cancellation_effective_date ? `Canceling — ends ${fmtDate(s.cancellation_effective_date)}` : s.status} t="mbx-status" />
          <Row k="Payment" v={s.payment_status || "—"} t="mbx-payment" />
          <Row k="Paid through" v={fmtDate(s.paid_through)} t="mbx-paid-through" />
          <Row k="Box access" v={s.entitled ? "Active" : "Not active (awaiting confirmed payment)"} t="mbx-entitled" />
          <Row k="Next billing" v={s.next_billing_date ? fmtDate(s.next_billing_date) : "—"} t="mbx-next-billing" />
          <Row k="Ships to" v={s.shipping_address ? [s.shipping_address.address_line_1, s.shipping_address.admin_area_2, s.shipping_address.admin_area_1, s.shipping_address.postal_code].filter(Boolean).join(", ") : "—"} t="mbx-shipping" />
          {data.latest_fulfillment && <Row k="Latest box" v={`${data.latest_fulfillment.period}: ${data.latest_fulfillment.status}`} t="mbx-fulfillment" />}
          {!s.cancellation_effective_date && ["ACTIVE", "PENDING", "PAUSED"].includes(s.status) && (
            <button onClick={cancel} disabled={busy} className="btn-ghost text-rose-300 mt-4" data-testid="mbx-cancel">{busy ? "Canceling…" : "Cancel subscription"}</button>
          )}
          {msg && <div className="text-xs text-white/70" data-testid="mbx-message">{msg}</div>}
        </div>
      )}
    </div>
  );
}

function Row({ k, v, t }) {
  return <div className="flex justify-between gap-4"><span className="font-bebas uppercase tracking-[0.18em] text-white/50">{k}</span><span data-testid={t}>{v}</span></div>;
}
